import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, RefreshCw, Download, Send, AlertTriangle, Banknote, CheckCircle } from 'lucide-react';
import { nominasAPI } from '@/api/api';
import { useToast } from '@/hooks/use-toast';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const formatoImporte = (valor) => {
  if (valor === null || valor === undefined) return 'Sin importe';
  return `${Number(valor).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
};

// Las fechas del backend son UTC sin zona (datetime.utcnow().isoformat())
const formatoFecha = (iso) => {
  if (!iso) return null;
  const texto = /Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(texto);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatoDia = (iso) => {
  if (!iso) return null;
  const texto = /Z|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(texto);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const mesInicial = (inicial) => {
  if (inicial && /^\d{4}-\d{2}$/.test(inicial)) {
    const [a, m] = inicial.split('-').map(Number);
    if (m >= 1 && m <= 12) return { anio: a, mes: m };
  }
  const hoy = new Date();
  return { anio: hoy.getFullYear(), mes: hoy.getMonth() + 1 };
};

const RemesaNominasModal = ({ open, onClose, mesInicialStr }) => {
  const { toast } = useToast();
  const [periodo, setPeriodo] = useState(() => mesInicial(mesInicialStr));
  const [remesa, setRemesa] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [avisando, setAvisando] = useState(false);
  const [marcando, setMarcando] = useState(false);

  useEffect(() => {
    if (open) setPeriodo(mesInicial(mesInicialStr));
  }, [open, mesInicialStr]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await nominasAPI.getRemesa(periodo.anio, periodo.mes);
      setRemesa(data && data.existe === false ? null : data);
    } catch (error) {
      setRemesa(null);
      toast({
        title: 'No se pudo cargar la remesa',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    } finally {
      setCargando(false);
    }
  }, [periodo, toast]);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const cambiarMes = (delta) => {
    setPeriodo(({ anio, mes }) => {
      let m = mes + delta;
      let a = anio;
      if (m < 1) { m = 12; a -= 1; }
      if (m > 12) { m = 1; a += 1; }
      return { anio: a, mes: m };
    });
  };

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await nominasAPI.generar(periodo.anio, periodo.mes);
      setRemesa(data);
      toast({
        title: 'Remesa generada',
        description: `${data.lineas?.length || 0} nóminas leídas de a3doc. Total ${formatoImporte(data.total)}.`,
      });
    } catch (error) {
      toast({
        title: 'No se pudo generar la remesa',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerando(false);
    }
  };

  const avisar = async () => {
    setAvisando(true);
    try {
      await nominasAPI.avisar(periodo.anio, periodo.mes);
      toast({ title: 'Aviso reenviado', description: 'Se ha enviado la notificación y el mensaje de Telegram.' });
      await cargar();
    } catch (error) {
      toast({
        title: 'No se pudo reenviar el aviso',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    } finally {
      setAvisando(false);
    }
  };

  const marcarPagada = async (pagada) => {
    setMarcando(true);
    try {
      const { data } = await nominasAPI.marcarPagada(periodo.anio, periodo.mes, pagada);
      setRemesa(data);
      toast({
        title: pagada ? 'Remesa marcada como pagada' : 'Remesa desmarcada',
        description: pagada ? 'Ya no aparecerá como pendiente en el Parte del día.' : 'Vuelve a figurar como pendiente de pagar.',
      });
    } catch (error) {
      toast({
        title: 'No se pudo actualizar el pago',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      });
    } finally {
      setMarcando(false);
    }
  };

  const descargar = () => {
    window.open(nominasAPI.urlExcel(periodo.anio, periodo.mes), '_blank');
    setTimeout(() => { cargar(); }, 2000);
  };

  if (!open) return null;

  const lineas = remesa?.lineas || [];
  const lineasValidas = lineas.filter((l) => l.en_plantilla && l.iban && l.importe !== null && l.importe !== undefined);
  const avisosGlobales = remesa?.avisos || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Banknote className="w-6 h-6 text-green-700" />
            <h2 className="text-xl font-bold text-gray-800">Remesa de nóminas</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Cerrar">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => cambiarMes(-1)}
                className="p-2 rounded border hover:bg-gray-50"
                aria-label="Mes anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="min-w-[160px] text-center font-semibold text-gray-800">
                {MESES[periodo.mes - 1]} {periodo.anio}
              </span>
              <button
                onClick={() => cambiarMes(1)}
                className="p-2 rounded border hover:bg-gray-50"
                aria-label="Mes siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={generar}
                disabled={generando || cargando}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${generando ? 'animate-spin' : ''}`} />
                {generando ? 'Leyendo a3doc...' : remesa ? 'Regenerar' : 'Generar desde a3doc'}
              </button>
              <button
                onClick={descargar}
                disabled={!remesa || lineasValidas.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar Excel BBVA
              </button>
              <button
                onClick={avisar}
                disabled={!remesa || avisando}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {avisando ? 'Enviando...' : 'Reenviar aviso'}
              </button>
              {remesa && !remesa.pagada_en && (
                <button
                  onClick={() => marcarPagada(true)}
                  disabled={marcando}
                  className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  {marcando ? 'Guardando...' : 'Marcar como pagada'}
                </button>
              )}
              {remesa?.pagada_en && (
                <button
                  onClick={() => marcarPagada(false)}
                  disabled={marcando}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {marcando ? 'Guardando...' : 'Desmarcar pagada'}
                </button>
              )}
            </div>
          </div>

          {remesa && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
              {formatoFecha(remesa.generado_en) && <span>Generada el {formatoFecha(remesa.generado_en)}</span>}
              <span>{formatoFecha(remesa.notificado_en) ? `Avisada el ${formatoFecha(remesa.notificado_en)}` : 'Sin avisar'}</span>
              <span>{formatoFecha(remesa.descargado_en) ? `Descargada el ${formatoFecha(remesa.descargado_en)}` : 'Sin descargar'}</span>
              {formatoDia(remesa.pagada_en) && (
                <span className="text-green-700 font-medium">Pagada el {formatoDia(remesa.pagada_en)}</span>
              )}
              <span className={remesa.completa ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
                {remesa.completa ? 'Completa' : 'Incompleta'}
              </span>
            </div>
          )}

          {avisosGlobales.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
              <div className="flex items-center gap-2 font-medium text-amber-800 mb-1">
                <AlertTriangle className="w-4 h-4" />
                Avisos
              </div>
              <ul className="list-disc list-inside text-sm text-amber-800">
                {avisosGlobales.map((a) => <li key={a}>{a}</li>)}
              </ul>
            </div>
          )}

          {cargando ? (
            <div className="text-center text-gray-500 py-10">Cargando...</div>
          ) : !remesa ? (
            <div className="text-center text-gray-500 py-10">
              Todavía no hay remesa de {MESES[periodo.mes - 1].toLowerCase()} de {periodo.anio}. Pulsa "Generar desde a3doc" para leer las nóminas.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-3 py-2">Código</th>
                    <th className="px-3 py-2">Beneficiario</th>
                    <th className="px-3 py-2">IBAN</th>
                    <th className="px-3 py-2 text-right">Importe</th>
                    <th className="px-3 py-2">Concepto</th>
                    <th className="px-3 py-2">Avisos</th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                        No hay nóminas de este mes en a3doc.
                      </td>
                    </tr>
                  )}
                  {lineas.map((l) => (
                    <tr
                      key={`${l.fila || 'fuera'}-${l.a3doc_document_id || l.nombre_beneficiario}`}
                      className={`border-t ${l.en_plantilla ? '' : 'bg-gray-50 text-gray-500'}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{l.en_plantilla ? l.codigo : '-'}</td>
                      <td className="px-3 py-2 font-medium text-gray-800">
                        {l.nombre_beneficiario}
                        {!l.en_plantilla && <div className="text-xs font-normal text-gray-500">Fuera del Excel</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{l.iban || '-'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{formatoImporte(l.importe)}</td>
                      <td className="px-3 py-2">{l.importe !== null && l.importe !== undefined ? l.concepto : '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(l.avisos || []).map((a) => (
                            <span key={a} className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">{a}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {lineas.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 font-semibold">
                      <td className="px-3 py-2" colSpan={3}>Total en el Excel</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{formatoImporte(remesa.total)}</td>
                      <td className="px-3 py-2" colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          <p className="text-xs text-gray-500">
            El ERP comprueba a3doc cada hora desde el día 20 y avisa en cuanto están todas las nóminas del mes.
          </p>

          <p className="text-xs text-gray-500">
            Código, nombre e IBAN vienen de la plantilla del banco; de cada nómina de a3doc solo se toma el líquido a percibir, sin redondeos.
            En el Excel solo cambian el importe y el concepto de cada fila. Un beneficiario sin nómina legible queda con el importe vacío,
            y un trabajador que no está en la plantilla hay que añadirlo a mano en el banco. Súbelo en la app del BBVA como remesa de nóminas.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RemesaNominasModal;
